import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LibraryUploadProps {
  label: string;
  setLabel: (val: string) => void;
  uploading: boolean;
  isMaxResumes: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onFileDrop?: (file: File) => Promise<void>;
}

export const LibraryUpload = ({ label, setLabel, uploading, isMaxResumes, onFileChange, onFileDrop }: LibraryUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isMaxResumes || uploading) return;
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true);
    } else if (e.type === 'dragleave') {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isMaxResumes || uploading) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        if (onFileDrop) await onFileDrop(file);
      } else {
        alert("Please upload a PDF file only.");
      }
    }
  };

  return (
    <div className="p-3 border-b border-slate-200 dark:border-slate-800">
      <div className="flex flex-col gap-2">
        {isMaxResumes && (
          <div className="text-[11px] bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-2 rounded border border-red-200 dark:border-red-900/50 font-medium leading-tight mb-1">
            You have reached the maximum limit of 10 resumes. Please delete an older resume to upload a new one.
          </div>
        )}
        <label htmlFor="resumeLabel" className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider px-1">Resume Label</label>
        <input
          id="resumeLabel"
          type="text"
          className="w-full text-xs font-medium border border-slate-200 dark:border-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 rounded-md px-3 py-2.5 bg-white dark:bg-slate-900/50 text-slate-900 dark:text-white placeholder-slate-400 outline-none disabled:opacity-50 disabled:bg-slate-50 dark:disabled:bg-slate-900"
          placeholder="e.g. Frontend 2026"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={isMaxResumes}
        />
        <label 
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={cn(
            "relative cursor-pointer w-full flex flex-col items-center justify-center gap-2 px-3 py-4 text-slate-700 dark:text-slate-300 text-xs font-medium rounded-md border-2 border-dashed focus-within:outline-none",
            isMaxResumes 
              ? "opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-900 border-solid border border-slate-200 dark:border-slate-700" 
              : isDragging 
                ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500 dark:border-emerald-500/50 text-emerald-700 dark:text-emerald-400" 
                : "border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 bg-white dark:bg-slate-900/40 hover:border-slate-400 dark:hover:border-slate-500"
          )}
        >
          {uploading ? (
            <span className="animate-spin inline-block w-5 h-5 border-[2px] border-t-transparent border-emerald-500 rounded-full shrink-0" />
          ) : (
            <Upload className={cn("w-5 h-5 shrink-0", isDragging ? "text-emerald-500 dark:text-emerald-400" : "text-slate-400 dark:text-slate-500")} />
          )}
          <span className="text-center leading-relaxed">
            {isDragging ? 'Drop PDF here...' : 'Click or drag PDF here'}
          </span>
          <input
            type="file"
            className="sr-only"
            accept=".pdf,application/pdf"
            disabled={uploading || isMaxResumes}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
                alert("Please upload a PDF file only.");
                e.target.value = '';
                return;
              }
              onFileChange(e);
            }}
          />
        </label>
      </div>
    </div>
  );
};
